import {EEventSource} from 'coreClasses/events/IEvents';
import {ESearchType} from 'coreClasses/search/ISearch';

export interface ISsrGlobalStyle {
	style: string;
	// Mounted instances using the rule
	usages: number;
	// Usages counted during SSR not yet claimed by a client mount (hydration)
	ssrUsages: number;
}

export type ISsrGlobalStyles = Record<string, ISsrGlobalStyle>;

export enum EHeaderType {
	DEFAULT = 'default',
	NAKED = 'naked',
	NAKED_MOBILE = 'naked_mobile',
	CONTRAST = 'contrast',
	TRANSPARENT = 'transparent',
	WHITE = 'white',
}

export enum ERequestClass {
	IOS_APP = 'iosApp',
	SPLASH_PAGE_CUSTOMER = 'SPLASH-',
	LIVE_PREVIEW = 'LIVE_PREVIEW',
	CMS_PREVIEW = 'CMS_PREVIEW',
	REMOTE = 'REMOTE',
	CORS = 'CORS',
	INSTANCE_PRODUCTION = 'INSTANCE_PRODUCTION',
	INSTANCE_STAGING = 'INSTANCE_STAGING',
	INSTANCE_DEVELOP = 'INSTANCE_DEVELOP',
	INSTANCE_SANDBOX = 'INSTANCE_SANDBOX',
	INSTANCE_LOCAL = 'INSTANCE_LOCAL',
}

export enum EInstanceKey {
	PRODUCTION = 'PRODUCTION',
	STAGING = 'STAGING',
	DEVELOP = 'DEVELOP',
	SANDBOX = 'SANDBOX',
	LOCAL = 'LOCAL',
}

export enum EOncloseActions {
	RESTORE_SIZE_SELECTOR = 'restoreSizeSelector',
	OPEN_MINICART = 'openMinicart',
	RESTORE_SIZE_SELECTOR_AND_CLOSE_ON_BACK = 'restoreSizeSelectorAndCloseOnBack',
}

export enum EHeaderRemoveItems {
	QUICK_SEARCH = 'quickSearch',
	MAIN_NAVIGATION = 'mainNavigation',
	MOBILE_NAVIGATION = 'mobileNavigation',
}

export type UIState = {
	flyout: string;
	currentLength?: string | null;
	sizeSelector?: {
		productID: string;
		variantID?: string;
		callback: (selectedVariationID: string) => void;
		moveFromWishlist: boolean;
		parentSelector: () => HTMLElement;
		countryCode?: string;
		hidePreorderSizes?: boolean;
		source?: EEventSource | null;
		length?: string | null;
	};
	sizeSelectorSelectedPID?: string | null;
	sizeSelectorSelectedValue?: string | null;
	sizeProductSelected?: string;
	colorProductSelected?: string;
	colorSelector?: {
		productID: string;
		variantID?: string;
		callback: (selectedVariationID: string) => void;
		parentSelector: () => HTMLElement;
		isPriceFiltered?: boolean;
	};
	colorSelectorSelectedPID?: string | null;
	bookAnAppointment: {
		storeId: String;
		storeEmail: String;
	};
	sizeGuide: {
		sizeGuideId: string;
		itemMeasurements?: string;
		onClose?: EOncloseActions;
		tabType?: string;
		productID?: string;
		fitInformation?: string;
		primaryCategoryName?: string;
	};
	notifyMe: {
		productID: string;
		selectedVariationID: string;
	};
	pdpZoom: {
		isActive: boolean;
		imageIndex?: number;
		clickedCoordinates?: {x: number; y: number};
		isSecondaryZoom?: boolean;
	};
	countryConfirmRedirectObj: {countryCode?: string; languageCode?: string; externalUrl?: string};
	openedPopupIds: string[];
	openedFlyoutIds: string[];
	rotationalBanner: boolean;
	badges: boolean;
	spinner: {
		spinner: boolean;
	};
	header: {
		show?: boolean;
		preventHeaderFromHiding?: boolean;
		locked?: boolean;
		type?: EHeaderType;
		isNavigationColorWhite?: boolean;
		isNavigationOpened?: boolean;
		itemsToHide?: EHeaderRemoveItems[];
		isWorldHeader?: boolean;
	};
	footer: {
		show?: boolean;
	};
	navigationStack?: Record<string, string>; // Record<navigationId, parentId>
	navigatingToCorrectLocale?: boolean;
	window: {
		viewportHeight: number;
	};
	isFooterClick: boolean;
	requestClasses: string[];
	isWishlistClick?: boolean;
	filtersOpened?: object;
	htmlCssClasses: [];
	mainContainerCssClasses: [];
	ssrGlobalStyles: ISsrGlobalStyles;
	addProductToWishlist?: IAddProductToWishlist | null;
	addProductToCart?: IAddProductToCart | null;
	moveProductFromBasketToWishlist?: IMoveProductFromBasketToWishlist | null;
	searchConfig: {
		searchType?: ESearchType;
		searchInstanceKey?: string;
	};
	backToTop?: boolean;
	scrollAfterLoading?: boolean;
	isServer: boolean;
	disableAddToCart: boolean;
	loginTabKey: string;
	loginFlyout: {
		titleKey: string;
	};
	pageViewCounter?: number;
	previousUrl?: string;
	storeDetail: {
		storeId?: string;
	};
	quickShop: {
		productID: string;
	};
	matchItWith?: {
		variationGroupId: string;
		selectedProductId?: string;
		preselectedVariantId?: string;
	};
	editAddressId: string | null;
	lockHeaderMovement?: boolean;
	personalizationFlyout: IPersonalizationFlyout;
	requiresLoginFeature: {
		enabled: boolean;
	};
};

export interface IPersonalizationFlyout {
	variationGroupID: string;
	variantID: string;
	message: string;
	basketItemId: string;
	number?: string;
	namePosition?: string;
	numberPosition?: string;
}

export interface IAddProductToWishlist {
	productId: string | null;
	variationGroupId: string;
	quantity: number;
	source: EEventSource | undefined;
	sourceSpecification?: string;
	shouldWaitToReset?: boolean;
	aggregateVariationGroup?: boolean;
}

export interface IAddProductToCart {
	productId: string | null;
	variationGroupId: string;
	size?: string;
	quantity: number;
	type: EAddToCartType;
	source: EEventSource | undefined;
	sourceSpecification?: string;
	parentSelector?: string;
}

export enum EAddToCartType {
	ADD_TO_CART = 'addToCart',
	BUY_NOW = 'buyNow',
	MOVE_FROM_WISHLIST = 'moveWishlistItemToBasket',
	APPLEPAY = 'ApplePay',
	GOOGLEPAY = 'GooglePay',
	PAYPAL = 'Paypal',
}

export interface IMoveProductFromBasketToWishlist {
	productId: string;
	variationGroupId: string;
	source: EEventSource | undefined;
	sourceSpecification?: string;
}
